import { axiosClient } from 'shared/apollo/request';

const BASE = '/v4/template-matcher';

export interface Template {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  description: string;
}

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface MatchResult {
  template_id: number;
  name: string | null;
  description?: string;
  score: number;
  rank: number;
}

export interface MatchResponse {
  match_id: number;
  matches: MatchResult[];
}

export type FeedbackType = 'rating' | 'no_match_closest_fit' | 'no_match_support';

export interface FeedbackResponse {
  id: number;
  match_id: number;
  rating: number;
  comment: string | null;
  feedback_type: FeedbackType;
  selected_template_id: number | null;
  created_at: string;
}

export interface CallerInfo {
  caller_ip: string | null;
  forwarded_for: string | null;
  user_agent: string | null;
  origin: string | null;
  referer: string | null;
}

export interface MatchQuery extends CallerInfo {
  id: number;
  query_text: string;
  created_at: string;
  matches: MatchResult[];
  feedback: {
    id: number;
    rating: number;
    comment: string | null;
    created_at: string;
  }[];
}

export interface FeedbackDetail extends CallerInfo {
  id: number;
  match_id: number;
  rating: number;
  comment: string | null;
  created_at: string;
  query_text: string;
}

export interface Stats {
  templates: number;
  match_queries: number;
  feedback_count: number;
  average_rating: number | null;
  rating_counts: Record<string, number>;
  top_templates: {
    template_id: number;
    name: string | null;
    appearances: number;
  }[];
  unused_templates: number;
}

export class AgentApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const request = async <T>(
  path: string,
  options: {
    method?: string;
    body?: unknown;
    params?: Record<string, string | number | boolean | undefined>;
  } = {},
): Promise<T> => {
  const { method = 'GET', body, params } = options;
  try {
    const response = await axiosClient.request<T>({
      method,
      url: `${BASE}${path}`,
      data: body,
      params,
    });
    return response.data;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    const status: number = err?.response?.status ?? 0;
    const data = err?.response?.data;
    const message: string =
      (typeof data === 'object' && data !== null && typeof data.message === 'string'
        ? data.message
        : null) ??
      err?.message ??
      `Request failed (${status})`;
    throw new AgentApiError(status, message);
  }
};

export interface ProfileMatchResponse extends Omit<MatchResponse, 'match_id'> {
  match_id: number | null;
  profile_query: string | null;
}

export const postMatch = (description: string, n: number) =>
  request<MatchResponse>('/match', {
    method: 'POST',
    body: { description, n },
  });

export const postMatchByProfile = (n: number, accountId?: number) =>
  request<ProfileMatchResponse>('/match-by-profile', {
    method: 'POST',
    body: { n, ...(accountId != null ? { account_id: accountId } : {}) },
  });

export const postFeedback = (
  match_id: number,
  rating: number,
  comment?: string,
  feedback_type?: FeedbackType,
  selected_template_id?: number,
) =>
  request<FeedbackResponse>('/feedback', {
    method: 'POST',
    body: {
      match_id,
      rating,
      ...(comment ? { comment } : {}),
      ...(feedback_type ? { feedback_type } : {}),
      ...(selected_template_id != null ? { selected_template_id } : {}),
    },
  });

export const getTemplates = (params: {
  limit: number;
  offset: number;
  q?: string;
}) => request<Page<Template>>('/templates', { params });

export const createTemplate = (name: string, description: string) =>
  request<Template>('/templates', {
    method: 'POST',
    body: { name, description },
  });

export const updateTemplate = (
  id: number,
  body: { name?: string; description?: string },
) =>
  request<Template>(`/templates/${id}`, {
    method: 'PATCH',
    body,
  });

export const deleteTemplate = (id: number) =>
  request<void>(`/templates/${id}`, { method: 'DELETE' });

export const getMatches = (params: {
  limit: number;
  offset: number;
  q?: string;
  only_with_feedback?: boolean;
  only_without_feedback?: boolean;
}) => request<Page<MatchQuery>>('/matches', { params });

export const getMatchDetail = (id: number) =>
  request<MatchQuery>(`/matches/${id}`);

export const getFeedbackList = (params: { limit: number; offset: number }) =>
  request<Page<FeedbackDetail>>('/feedback', { params });

export const getStats = () => request<Stats>('/stats');
